import { logAssert } from "@faire/web/common/logging";
import { useRefFrom } from "@faire/web/ui/hooks/useRefFrom";
import React, { useContext, useEffect } from "react";
import { useLocation, useRouteMatch } from "react-router-dom";

import { useLoggedOutCategoryPageCurrentNavigationPath } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/Context/useLoggedOutCategoryPageCurrentNavigationPath";

import { LoggedOutMarketplaceBrandSearchModel } from "./LoggedOutMarketplaceBrandSearchModel";

export const LoggedOutMarketplaceBrandSearchModelContext = React.createContext<
  LoggedOutMarketplaceBrandSearchModel | undefined
>(undefined);

const useViewState = () => {
  const location = useLocation();
  const match = useRouteMatch();
  const navigationPath = useLoggedOutCategoryPageCurrentNavigationPath();

  const viewState = useRefFrom(
    () =>
      new LoggedOutMarketplaceBrandSearchModel(navigationPath, location, match)
  ).current;

  useEffect(() => {
    viewState.init();
  }, [viewState]);

  useEffect(() => {
    viewState.updateLocationHistoryAndMatch(location, match);
  }, [location, viewState, match]);

  useEffect(() => {
    viewState.processNewUrl();
  }, [location.pathname, location.search, viewState]);

  return viewState;
};

export const LoggedOutCategoryPageViewStateProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const viewState = useViewState();
  return (
    <LoggedOutMarketplaceBrandSearchModelContext.Provider value={viewState}>
      {children}
    </LoggedOutMarketplaceBrandSearchModelContext.Provider>
  );
};

export const useLoggedOutCategoryPageViewStateContext = () => {
  const marketplaceBrandSearchModel = useContext(
    LoggedOutMarketplaceBrandSearchModelContext
  );
  logAssert(
    !!marketplaceBrandSearchModel,
    "marketplaceBrandSearchModel should not be undefined"
  );
  return marketplaceBrandSearchModel;
};
